import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ChevronRight } from "lucide-react";

// Compact header row that opens its full content in a dialog, keeping the chart short.
export const ChartSection = ({ icon: Icon, title, summary, badge, tone = "muted", testId, hash, children, onOpenChange }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { if (hash && location.hash === `#${hash}`) setOpen(true); else if (hash && location.hash) setOpen(false); }, [location.hash, hash]);
  const tones = { muted: "bg-muted text-muted-foreground", good: "bg-emerald-100 text-emerald-800", warn: "bg-amber-100 text-[#91661E]", bad: "bg-red-100 text-[#A4473C]", brand: "bg-secondary text-primary" };
  const close = (o) => { setOpen(o); onOpenChange?.(o); if (!o && hash && window.location.hash === `#${hash}`) window.history.replaceState(null, "", window.location.pathname); };
  return (
    <>
      <button type="button" onClick={() => { setOpen(true); onOpenChange?.(true); }} className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60" data-testid={testId}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-primary"><Icon className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{title}</span>{summary && <span className="block truncate text-xs text-muted-foreground">{summary}</span>}</span>
        {badge !== undefined && badge !== null && <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${tones[tone]}`} data-testid={`${testId}-badge`}>{badge}</span>}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-h-[88vh] w-[min(96vw,760px)] max-w-none overflow-y-auto rounded-3xl border-0 bg-card p-7 shadow-[0_24px_80px_-12px_rgba(24,54,47,0.35)] sm:rounded-3xl" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-lg"><Icon className="h-5 w-5 text-primary" /> {title}</DialogTitle></DialogHeader>
          <div className="text-[15px] leading-relaxed [&_.card-r]:border-0 [&_.card-r]:bg-transparent [&_.card-r]:p-0 [&_.card-r]:shadow-none">{open && children}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function useChartSummary(clientId, deps = []) {
  const [s, setS] = useState({});
  useEffect(() => {
    let live = true;
    Promise.allSettled([
      api.get(`/clients/${clientId}/chart-audit`), api.get(`/clients/${clientId}/uploads`), api.get(`/clients/${clientId}/authorizations`), api.get(`/clients/${clientId}/awaiting-signature`),
    ]).then(([a, u, au, aw]) => {
      if (!live) return;
      const audit = a.status === "fulfilled" ? a.value.data : null;
      const uploads = u.status === "fulfilled" ? u.value.data : [];
      const auths = au.status === "fulfilled" ? au.value.data.items || [] : [];
      const awaiting = aw.status === "fulfilled" ? (aw.value.data || []).length : 0;
      const cur = auths.find((x) => ["Active", "Expiring"].includes(x.status)) || auths[0];
      setS({ audit, uploads: Array.isArray(uploads) ? uploads.length : (uploads.items || []).length, auth: cur, auths: auths.length, awaiting });
    });
    return () => { live = false; };
    // eslint-disable-next-line
  }, [clientId, ...deps]);
  return s;
}
